import {
  type CompanyData,
  type Dimensions,
  type InputValue,
  type InteractionLog,
  type TestSuiteRecordingApp,
} from "../../utils/types";
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { styled as styledMaterial } from "@mui/material/styles";
import { TabPanel } from "@mui/lab";
import {
  annotateScreenshot,
  getBase64FromImageUrl,
  parseGptCommandsResponse,
  replaceEnvVarsAndRunInputs,
  replacePreRequestRememberedValues,
} from "../../utils/helpers";
import { useSetAtom, useAtomValue } from "jotai/index";
import { logDetailDialogStateAtom } from "../../components/test-editor-gpt/execution-logs/logDetailDialog";
import ReasoningAndCommands from "../../components/reasoningAndCommands";
import { IconButton, Paper, ThemeProvider, Tooltip, Zoom } from "@mui/material";
import styled from "@emotion/styled";
import { CustomFab } from "./components";
import { CameraAlt, North, South, ZoomIn } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { forceMoveToTimestampNetworkTabAtom } from "./networkLogsTab";
import { forceMoveToTimestampDeviceTabAtom } from "./consoleLogsTab";
import CropScreenshotDialog, {
  CropScreenshotDialogStateAtom,
} from "../../components/cropScreenshotDialog";
import ZoomImageDialog, {
  zoomImageDialogStateAtom,
} from "../../components/zoomImageDialog";
import { lightTheme } from "../../themes";
import { AuthContext } from "../../auth/AuthContext";
import { companyDataAtom } from "../test-run/test-run-atoms";
import { useLoaderData } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { PlayVoiceCommandCustomButton } from "@/components/test-editor-gpt/execution-logs/testEditorExecutionLogs";

interface InteractionLogsTabProps {
  interactionLogs: InteractionLog[];
  playbackTimestamp: number;
  display: boolean;
  setTabValue: Dispatch<SetStateAction<string>>;
  testInputs: InputValue[];
  companyData?: CompanyData;
  preRequestRememberedKeys: string[];
  deviceScreen?: Dimensions;
}

const InteractionLogsTab = ({
  interactionLogs,
  playbackTimestamp,
  display,
  setTabValue,
  testInputs,
  companyData,
  preRequestRememberedKeys,
  deviceScreen,
}: InteractionLogsTabProps) => {
  const [jumpToTimestamp, setJumpToTimestamp] = useState<
    "hidden" | "top" | "bottom"
  >("hidden");
  const parentRef = React.useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: interactionLogs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => {
      const BASE_HEIGHT = 60; // Reduce from 80
      const TIMESTAMP_ROW = 50; // Reduce from 60
      const TITLE_HEIGHT = 25; // Reduce from 30
      const PROPERTY_WRAPPER_PADDING = 16; // Reduce from 20
      const LIST_ITEM_HEIGHT = 20; // Reduce from 24
      const LINE_HEIGHT = 18; // Reduce from 20

      let estimatedHeight = BASE_HEIGHT + TIMESTAMP_ROW;
      const interaction = interactionLogs[i];
      const prevInteraction = interactionLogs[i - 1];

      // Parse the interaction data (same as your component does)
      const { reasoning, commands, step_number, fromCache, step_description } =
        parseGptCommandsResponse(interaction.gptCommands ?? []);

      // Process commands with env vars and inputs
      let parsedCommands =
        companyData == null
          ? commands
          : replaceEnvVarsAndRunInputs({
              command: commands,
              inputValues: testInputs,
              envVars: companyData.settings.envVars,
            });

      // Apply pre-request replacements if needed
      if (
        companyData != null &&
        interaction.preRequestResponse != null &&
        prevInteraction != null
      ) {
        parsedCommands = replacePreRequestRememberedValues({
          allowedValues: preRequestRememberedKeys,
          command: parsedCommands,
          previousInteraction: prevInteraction,
        });
      }

      // Step number (if exists)
      if (step_number) {
        estimatedHeight += TITLE_HEIGHT;
      }

      // Step description (if exists)
      if (step_description) {
        const descriptionLines = Math.ceil(step_description.length / 80);
        estimatedHeight +=
          TITLE_HEIGHT +
          PROPERTY_WRAPPER_PADDING +
          descriptionLines * LINE_HEIGHT;
      }

      // From cache indicator (if exists)
      if (fromCache) {
        estimatedHeight += TITLE_HEIGHT;
      }

      // Commands section (always present)
      const commandLines = parsedCommands
        .split("\n")
        .filter((line) => line.trim()).length;
      estimatedHeight +=
        TITLE_HEIGHT +
        PROPERTY_WRAPPER_PADDING +
        commandLines * LIST_ITEM_HEIGHT;

      // Reasoning section
      const reasoningItems = reasoning
        .replace("reasoning:", "")
        .split(" - ")
        .filter((item) => item).length;

      if (reasoningItems > 0) {
        estimatedHeight +=
          TITLE_HEIGHT +
          PROPERTY_WRAPPER_PADDING +
          reasoningItems * LIST_ITEM_HEIGHT;
      }

      // Pre-request response section
      if (interaction.preRequestResponse != null) {
        estimatedHeight += TITLE_HEIGHT + PROPERTY_WRAPPER_PADDING;

        // Status code line
        if (interaction.preRequestResponse.status) {
          estimatedHeight += LIST_ITEM_HEIGHT;
        }

        // JSON syntax highlighter - estimate based on JSON size
        const jsonString = JSON.stringify(
          interaction.preRequestResponse,
          null,
          1
        );
        const jsonLines = jsonString.split("\n").length;
        estimatedHeight += Math.min(jsonLines * 18, 400); // Cap at 400px for large JSONs
      }

      const MIN_HEIGHT =
        BASE_HEIGHT +
        TIMESTAMP_ROW +
        TITLE_HEIGHT + // Step number
        TITLE_HEIGHT +
        PROPERTY_WRAPPER_PADDING +
        LINE_HEIGHT * 2 + // Prompt (2 lines average)
        TITLE_HEIGHT +
        PROPERTY_WRAPPER_PADDING +
        LIST_ITEM_HEIGHT * 1 + // Commands (1 line)
        TITLE_HEIGHT +
        PROPERTY_WRAPPER_PADDING +
        LIST_ITEM_HEIGHT * 7; // Reasoning (5 bullets)

      return Math.max(estimatedHeight, MIN_HEIGHT);
    },
    overscan: 4,
    enabled: true,
    gap: 16,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  // Update jumpToTimestamp based on visible items
  useEffect(() => {
    if (!virtualItems.length || !parentRef.current) return;

    const scrollTop = parentRef.current.scrollTop;
    const viewportHeight = parentRef.current.clientHeight;
    const scrollBottom = scrollTop + viewportHeight;

    // Check if playback timestamp is before first log
    if (
      interactionLogs?.at(0)?.relativeTimestamp != null &&
      playbackTimestamp <= interactionLogs.at(0)!.relativeTimestamp
    ) {
      setJumpToTimestamp("hidden");
      return;
    }

    // Find the index of the item at the current timestamp
    const currentTimestampIndex = interactionLogs.findLastIndex(
      (log) => log.relativeTimestamp <= playbackTimestamp
    );

    if (currentTimestampIndex === -1) {
      setJumpToTimestamp("hidden");
      return;
    }

    // Get the virtual item for this index (if it exists in virtual items)
    const currentItem = virtualItems.find(
      (item) => item.index === currentTimestampIndex
    );

    // If item is not in virtual items yet, determine direction
    if (!currentItem) {
      const firstVisibleIndex = virtualItems[0].index;
      setJumpToTimestamp(
        currentTimestampIndex < firstVisibleIndex ? "top" : "bottom"
      );
      return;
    }

    // Check if the current timestamp item is visible in viewport
    const itemTop = currentItem.start;
    const itemBottom = currentItem.start + currentItem.size;

    const isItemFullyVisible =
      itemTop >= scrollTop && itemBottom <= scrollBottom;
    const isItemPartiallyVisible =
      (itemTop >= scrollTop && itemTop <= scrollBottom) ||
      (itemBottom >= scrollTop && itemBottom <= scrollBottom) ||
      (itemTop <= scrollTop && itemBottom >= scrollBottom);

    // Check if we're past the last item
    const lastItemDisplayed =
      virtualItems[virtualItems.length - 1].index ===
      interactionLogs.length - 1;
    const isPastLastItem =
      playbackTimestamp > interactionLogs.at(-1)!.relativeTimestamp &&
      lastItemDisplayed;

    if (isItemFullyVisible || isItemPartiallyVisible || isPastLastItem) {
      setJumpToTimestamp("hidden");
    } else {
      // Determine direction based on position relative to viewport
      setJumpToTimestamp(itemTop < scrollTop ? "top" : "bottom");
    }
  }, [playbackTimestamp, interactionLogs, virtualItems]);

  const jumpToTimestampHandler = useCallback(() => {
    const indexToJump = interactionLogs.findLastIndex(
      (log) => playbackTimestamp >= log.relativeTimestamp
    );

    if (indexToJump === -1) return;

    const extraItemExists = !!interactionLogs[indexToJump + 1];

    const targetIndex =
      jumpToTimestamp === "bottom"
        ? indexToJump + (extraItemExists ? 1 : 0)
        : indexToJump;

    rowVirtualizer.scrollToIndex(targetIndex, {
      align: jumpToTimestamp === "bottom" ? "end" : "start",
      behavior: "smooth",
    });
  }, [interactionLogs, playbackTimestamp, jumpToTimestamp, rowVirtualizer]);

  const setForceMoveToTimestampNetworkTab = useSetAtom(
    forceMoveToTimestampNetworkTabAtom
  );

  const setForceMoveToTimestampDeviceTab = useSetAtom(
    forceMoveToTimestampDeviceTabAtom
  );

  const handleMoveToNetworkLog = useCallback((timestamp: number) => {
    setTabValue("1");
    setForceMoveToTimestampNetworkTab(timestamp);
  }, []);

  const handleMoveToDeviceLog = useCallback((timestamp: number) => {
    setTabValue("2");
    setForceMoveToTimestampDeviceTab(timestamp);
  }, []);

  return (
    <>
      <ThemeProvider theme={lightTheme}>
        <ZoomImageDialog deviceScreen={deviceScreen} />
        <CropScreenshotDialog />
      </ThemeProvider>

      <InteractionLogsWrapper keepMounted value={"0"} display={display}>
        <div
          ref={parentRef}
          style={{
            overflow: "auto",
            height: "100%",
            width: "100%",
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualItems?.map((virtualRow) => {
              const currentLog = interactionLogs[virtualRow.index];
              const prevLog = interactionLogs[virtualRow.index - 1];
              const className =
                virtualRow.index === interactionLogs.length - 1 &&
                playbackTimestamp > currentLog.relativeTimestamp
                  ? "afterLastItem"
                  : currentLog.relativeTimestamp <= playbackTimestamp
                  ? "beforeCurrentTime"
                  : "afterCurrentTime";

              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <InteractionLogItems
                    className={className}
                    handleMoveToNetworkLog={handleMoveToNetworkLog}
                    interaction={currentLog}
                    prevInteraction={prevLog}
                    handleMoveToDeviceLog={handleMoveToDeviceLog}
                    testInputs={testInputs}
                    companyData={companyData}
                    preRequestRememberedKeys={preRequestRememberedKeys}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <Zoom in={jumpToTimestamp !== "hidden"} unmountOnExit>
          <CustomFab
            variant={"extended"}
            jumpToTimestamp={jumpToTimestamp}
            onClick={jumpToTimestampHandler}
          >
            {jumpToTimestamp !== "top" ? <South /> : <North />}
            Jump to current timestamp
          </CustomFab>
        </Zoom>
      </InteractionLogsWrapper>
    </>
  );
};

const InteractionLogsWrapper = styledMaterial(TabPanel)(
  ({ display }: { display: boolean }) =>
    display
      ? {
          gridArea: "tab-content",
          padding: "16px",
          overflow: "auto",
          position: "relative",
          height: "100%",
          "& > div:first-of-type > div > div:has(.beforeCurrentTime) + div .afterCurrentTime::before":
            {
              borderTop: "3px solid #90caf9",
              content: '""',
              left: "0px",
              position: "absolute",
              top: "-9px",
              width: "100%",
            },
        }
      : {}
);

function InteractionLogItems({
  interaction,
  prevInteraction,
  handleMoveToNetworkLog,
  handleMoveToDeviceLog,
  testInputs,
  companyData,
  preRequestRememberedKeys,
  className,
}: {
  interaction: InteractionLog;
  prevInteraction?: InteractionLog;
  handleMoveToNetworkLog: (timestamp: number) => void;
  handleMoveToDeviceLog: (timestamp: number) => void;
  testInputs: InputValue[];
  companyData?: CompanyData;
  preRequestRememberedKeys: string[];
  className: string;
}) {
  const { data: parsedScreenshot } = useQuery({
    queryKey: ["annotateScreenshotRecording", interaction.screenshot],
    queryFn: async () => {
      const parsedCommands = parseGptCommandsResponse(
        interaction.gptCommands ?? []
      ).commands;

      const nonAnnotatedScreenshot = `data:image/jpeg;base64,${await getBase64FromImageUrl(
        interaction.screenshot!
      )}`;

      const annotatedScreenshot = await annotateScreenshot(
        interaction.screenshot!,
        interaction.appetizeCommands ?? parsedCommands.split("\n")
      );

      return { nonAnnotatedScreenshot, annotatedScreenshot };
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const processedScreenshot =
    parsedScreenshot?.annotatedScreenshot ?? interaction.screenshot;

  const setLogDetailDialogState = useSetAtom(logDetailDialogStateAtom);

  const { reasoning, commands, step_number, fromCache, step_description } =
    parseGptCommandsResponse(interaction.gptCommands ?? []);

  let parsedCommands =
    companyData == null
      ? commands
      : replaceEnvVarsAndRunInputs({
          command: commands,
          inputValues: testInputs,
          envVars: companyData.settings.envVars,
        });

  if (companyData != null && interaction.preRequestResponse != null) {
    interaction.preRequestResponse.curl = replaceEnvVarsAndRunInputs({
      command: interaction.preRequestResponse.curl,
      inputValues: testInputs,
      envVars: companyData.settings.envVars,
    });

    if (prevInteraction != null) {
      interaction.preRequestResponse.curl = replacePreRequestRememberedValues({
        allowedValues: preRequestRememberedKeys,
        command: interaction.preRequestResponse.curl,
        previousInteraction: prevInteraction,
      });

      parsedCommands = replacePreRequestRememberedValues({
        allowedValues: preRequestRememberedKeys,
        command: parsedCommands,
        previousInteraction: prevInteraction,
      });
    }
  }

  const onClickHandler = useCallback(() => {
    if (interaction.serverExecutionParams != null) {
      setLogDetailDialogState({
        open: true,
        isBaseline: false,
        redefiningStep: true,
        log: {
          annotatedScreenshotUrl: processedScreenshot,
          screenshotUrl: interaction.screenshot!,
          appetizeCommand: "",
          gptCommand: commands,
          serverExecutionParams: interaction.serverExecutionParams,
          reasoning,
          stepNumber: step_number,
          fromCache,
        },
      });
    } else if (interaction.preRequestResponse != null) {
      setLogDetailDialogState({
        open: true,
        isBaseline: false,
        redefiningStep: false,
        log: {
          annotatedScreenshotUrl: processedScreenshot,
          screenshotUrl: interaction.screenshot!,
          appetizeCommand: "Executed network request",
          prompt: "Executing network request",
          gptCommand: interaction.preRequestResponse.curl,
          preRequestResponse: JSON.stringify(
            interaction.preRequestResponse.data,
            null,
            1
          ),
          statusCode: interaction.preRequestResponse.status,
          fromCache: false,
          title: "pre-requests",
        },
      });
    }
  }, [interaction, processedScreenshot]);

  const setZoomImageDialogState = useSetAtom(zoomImageDialogStateAtom);
  const useAuth = () => useContext<any>(AuthContext);
  const { user } = useAuth();

  return (
    <InteractionLogItemWrapper className={className} elevation={2}>
      <InfoWrapper>
        <ReasoningAndCommands
          promptActionHandler={user.isAnonymous ? undefined : onClickHandler}
          relativeTimestamp={interaction.relativeTimestamp}
          handleMoveToNetworkLog={handleMoveToNetworkLog}
          handleMoveToDeviceLog={handleMoveToDeviceLog}
          reasoning={reasoning
            .replace("reasoning:", "")
            .split(" - ")
            .filter((item) => item)}
          commands={parsedCommands}
          stepNumber={step_number}
          fromCache={fromCache}
          stepDescription={step_description}
          absoluteTimestamp={interaction.absoluteTimestamp}
          preRequestResponse={interaction.preRequestResponse}
        />
      </InfoWrapper>
      <ScreenshotWrapper>
        <CustomImage src={processedScreenshot}></CustomImage>
        <ActionsRow>
          {!user.firebaseUser.isAnonymous &&
            parsedScreenshot?.nonAnnotatedScreenshot && (
              <CreateTemplateWrapper
                imageSrc={parsedScreenshot.nonAnnotatedScreenshot}
              />
            )}
          <Tooltip title={"Enlarge image"} placement={"bottom"}>
            <span>
              <CustomActionButton
                aria-label="Enlarge image"
                size="small"
                onClick={(event) => {
                  setZoomImageDialogState({
                    open: true,
                    imageSrc: parsedScreenshot?.nonAnnotatedScreenshot,
                    uiElements:
                      interaction.serverExecutionParams?.uiElements ?? [],
                  });
                }}
              >
                <ZoomIn />
              </CustomActionButton>
            </span>
          </Tooltip>
          {(interaction.voiceCommandUrl ?? interaction.listenedAudioUrl) && (
            <PlayVoiceCommandCustomButton
              voiceCommandUrl={
                (interaction.voiceCommandUrl ?? interaction.listenedAudioUrl)!
              }
            />
          )}
        </ActionsRow>
      </ScreenshotWrapper>
    </InteractionLogItemWrapper>
  );
}

const CreateTemplateWrapper = ({ imageSrc }: { imageSrc: string }) => {
  const loaderData = useLoaderData() as TestSuiteRecordingApp;
  const { data: testSuiteData } = useAtomValue(loaderData.testSuiteDataAtom);
  const { data: testRunData } = useAtomValue(loaderData.testRunDataAtom);
  const { data: companyData } = useAtomValue(
    companyDataAtom(testSuiteData!.organisationId)
  );

  const setCropScreenshotDialogState = useSetAtom(
    CropScreenshotDialogStateAtom
  );

  return (
    <>
      {companyData != null && (
        <Tooltip title={"Create template"} placement={"bottom"}>
          <span>
            <CustomActionButton
              aria-label="create template"
              size="small"
              onClick={(event) => {
                setCropScreenshotDialogState({
                  open: true,
                  imageSrc,
                  companyData,
                  deviceName: testRunData!.runs.at(0)!.device,
                });
              }}
            >
              <CameraAlt />
            </CustomActionButton>
          </span>
        </Tooltip>
      )}
    </>
  );
};

const CustomActionButton = styled(IconButton)(() => ({
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },
  transition: "opacity 300ms",
}));

const InteractionLogItemWrapper = styledMaterial(Paper)(() => ({
  display: "grid",
  gridTemplateAreas: '"info screenshot"',
  gridTemplateColumns: "minmax(0,1fr) 240px",
  gap: "24px",
  padding: "16px",
  position: "relative",
  height: "100%",
}));

const CustomImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  min-height: 400px;
  width: auto;
  height: auto;
`;

const InfoWrapper = styled.p`
  grid-area: info;
`;

const ScreenshotWrapper = styled.div`
  grid-area: screenshot;
  position: relative;
  overflow: hidden;

  &:hover button {
    opacity: 1;
  }

  & button {
    opacity: 0;
  }
`;

// transform to object
const ActionsRow = styled.div`
  visibility: visible;
  position: absolute;
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  top: 4px;
  right: 4px;
  width: 100%;
`;

export { CustomImage };
export default InteractionLogsTab;
